import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { LoginPage } from '../modules/Auth/views/LoginPage';
import { AuthUserProvider, useAuth } from '../providers/AuthUserProvider';
import { CoenergyUserRole, staffRoles } from '../types/CoenergyUserRole';

import { AppPrivate } from './AppPrivate';
import { AppPublic } from './AppPublic';
import { AppRolePrivate } from './AppRolePrivate';
import { getDefaultPathForRole } from './getDefaultPathForRole';
import {
  AdminPartnerBulkImportPage,
  AdminPartnerClientDataImportPage,
  AdminPartnerClientsPage,
  AdminPartnerExportPage,
  AdminPartnerListPage,
  AdminPartnerPortalLayout,
  AdminContractsPage,
  AdminSellersPage,
  AdminWithdrawalsPage,
  HomePage,
  PartnerClientDetailLegacyRedirect,
  PartnerClientDetailPage,
  PartnerCommissionOverviewPage,
  PartnerCommissionsShell,
  PartnerInvoicesListPage,
  PartnerPortalLayout,
  PartnerProfilePage,
  PartnerResiduesPage,
} from './lazy-pages';
import { Paths } from './Paths';

export const Loader = ({ open = false }: { open?: boolean }) => {
  if (!open) {
    return null;
  }

  return (
    <div className="loader-backdrop">
      <div className="loader-spinner" />
    </div>
  );
};

const RootRedirect = () => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return <Navigate to={getDefaultPathForRole(user.role)} replace />;
};

export const AppRoutes = () => {
  return (
    <BrowserRouter>
      <React.Suspense fallback={<Loader open />}>
        <AuthUserProvider>
          <Routes>
            <Route element={<AppPublic />}>
              <Route path={Paths.Login} element={<LoginPage />} />
            </Route>

            <Route element={<AppPrivate />}>
              <Route path={Paths.Root} element={<RootRedirect />} />

              <Route
                element={
                  <AppRolePrivate
                    allowedRoles={staffRoles}
                    fallbackPath={Paths.Partner}
                  />
                }
              >
                <Route path={Paths.Home} element={<HomePage />} />
              </Route>

              <Route
                element={
                  <AppRolePrivate
                    allowedRoles={[CoenergyUserRole.Admin]}
                    fallbackPath={Paths.Home}
                  />
                }
              >
                <Route
                  path={Paths.AdminPartners}
                  element={<AdminPartnerListPage />}
                />
                <Route
                  path={Paths.AdminSellers}
                  element={<AdminSellersPage />}
                />
                <Route
                  path={Paths.AdminContracts}
                  element={<AdminContractsPage />}
                />
                <Route
                  path={Paths.AdminPayments}
                  element={<AdminWithdrawalsPage />}
                />
                <Route
                  path={Paths.AdminWithdrawals}
                  element={<Navigate to={Paths.AdminPayments} replace />}
                />
                <Route
                  path={`${Paths.AdminPartners}/:partnerId`}
                  element={<AdminPartnerPortalLayout />}
                >
                  <Route element={<PartnerCommissionsShell />}>
                    <Route
                      index
                      element={<Navigate to="visao-geral" replace />}
                    />
                    <Route
                      path="visao-geral"
                      element={<PartnerCommissionOverviewPage />}
                    />
                  </Route>
                  <Route path="residuos" element={<PartnerResiduesPage />} />
                  <Route path="perfil" element={<PartnerProfilePage />} />
                  <Route path="faturas" element={<PartnerInvoicesListPage />} />
                  <Route
                    path="faturas/:clientId"
                    element={<PartnerClientDetailPage />}
                  />
                  <Route
                    path="clientes"
                    element={<AdminPartnerClientsPage />}
                  />
                  <Route path="exportar" element={<AdminPartnerExportPage />} />
                  <Route
                    path="importar-clientes"
                    element={<AdminPartnerBulkImportPage />}
                  />
                  <Route
                    path="importar-dados-cliente"
                    element={<AdminPartnerClientDataImportPage />}
                  />
                  <Route
                    path="clientes/:clientId"
                    element={<PartnerClientDetailLegacyRedirect />}
                  />
                </Route>
              </Route>

              <Route
                element={
                  <AppRolePrivate
                    allowedRoles={[CoenergyUserRole.Partner]}
                    fallbackPath={Paths.Home}
                  />
                }
              >
                <Route path={Paths.Partner} element={<PartnerPortalLayout />}>
                  <Route element={<PartnerCommissionsShell />}>
                    <Route
                      index
                      element={<Navigate to="visao-geral" replace />}
                    />
                    <Route
                      path="visao-geral"
                      element={<PartnerCommissionOverviewPage />}
                    />
                  </Route>
                  <Route path="residuos" element={<PartnerResiduesPage />} />
                  <Route path="perfil" element={<PartnerProfilePage />} />
                  <Route path="faturas" element={<PartnerInvoicesListPage />} />
                  <Route
                    path="faturas/:clientId"
                    element={<PartnerClientDetailPage />}
                  />
                  <Route
                    path="clientes/:clientId"
                    element={<PartnerClientDetailLegacyRedirect />}
                  />
                </Route>
              </Route>
            </Route>
          </Routes>
        </AuthUserProvider>
      </React.Suspense>
    </BrowserRouter>
  );
};
